import { QuestionTypeEnum } from '@doltech/utils/lib/constants';
import { isNumber } from '@doltech/utils/lib/number';
import { numberToABC } from '@doltech/utils/lib/text';
import cl from 'classnames';
import * as React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import styled from 'styled-components';

import { namedColors } from '../colors';
import { colorsV2 } from '../colors-v2';
import { getEmptyImage } from '../FloatingDraggableBox/FloatingDraggableBox';
import { SmallCloseButton, SpotItem, SpotItemWithBorder, SpotPlaceholder } from './SpotEditor';
import { ResizableImage, Spot } from './types';
import { OptionListWithTitleLayout } from '../Options/OptionListWithTitleLayout';
import { ScrollableOptionListLayout } from '../Options/ScrollableOptionListLayout';
import { shadows } from '../shadows';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';

const FloatingRight = styled.div`
  position: sticky;
  top: 0;

  background-color: ${colorsV2.white100};
  border-radius: 8px;
  box-shadow: ${shadows.zIndex4};

  &.outer {
    margin-left: 0;
    ${fromScreen(1024)} {
      transform: translateX(calc(100% + 30px));
    }
  }
`;

export interface DragOutProps extends React.HTMLAttributes<HTMLDivElement> {
  no?: number;
  answers: number[];
  spots: Spot[];
  image: ResizableImage;
  questions: string[];
  onRemoveAnswer: (idx: number) => void;
  onAnswer: (idx: number, spotIdx: number) => void;
}

const DragOutContext = React.createContext<DragOutProps>(null);

const DragOut = (props: DragOutProps, floatingBoxRef: any) => {
  const { questions, id } = props;

  const [showOptions, setShowOptions] = React.useState(false);

  React.useEffect(() => {
    const clientHeight = document.getElementById(id)?.clientHeight;
    if (clientHeight) {
      setShowOptions(true);
    }
  }, [id]);

  return (
    <DragOutContext.Provider value={props}>
      <Container id={id}>
        <Image />
        {showOptions && (
          <FloatingRight className="outer">
            <OptionList>
              <OptionListWithTitleLayout title="Kéo option từ hình vào đây">
                <ScrollableOptionListLayout>
                  {questions.map((question, idx) => (
                    <Question key={`${question}-${idx}`} idx={idx} question={question} />
                  ))}
                </ScrollableOptionListLayout>
              </OptionListWithTitleLayout>
            </OptionList>
          </FloatingRight>
        )}
      </Container>
    </DragOutContext.Provider>
  );
};

const Container = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 0;
  gap: 16px;
`;

const OPTION_LIST_WIDTH = 268;

const OptionList = styled.div`
  height: calc(100% + 32px);

  width: ${OPTION_LIST_WIDTH}px;
  background-color: ${namedColors.pureWhite};
  box-shadow: 0 16px 24px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.04),
    0 0 1px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  position: absolute;
  left: calc(100% + 8px);
  top: -16px;
  z-index: 2;
`;

export default React.memo(React.forwardRef(DragOut));

// =================================================================================
// DraggableSpot
// =================================================================================

interface SpotProps extends React.HTMLAttributes<HTMLDivElement> {
  spot: Spot;
}

const DraggableSpot = (props: SpotProps) => {
  const { spot, className, ...rest } = props;

  const { x, y, text } = spot;

  const [{ isDragging }, drag, preview] = useDrag({
    type: QuestionTypeEnum.DRAG_OUT,
    item: () => ({
      spot,
    }),
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging(),
      };
    },
  });

  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Subtract 4px for the wrapper div with the extended border.
  return (
    <DraggableSpotContainer style={{ top: y - 4, left: x - 4 }}>
      <DragOutSpot
        ref={drag}
        text={text}
        className={cl(className, { dragging: isDragging })}
        {...rest}
      />
    </DraggableSpotContainer>
  );
};

export const MobileSpot = (props: SpotProps) => {
  const { spot, className, ...rest } = props;
  const { x, y, text } = spot;

  return (
    <MobileDraggableSpotContainer style={{ top: y, left: x }}>
      <DragOutSpot text={text} {...rest} />
    </MobileDraggableSpotContainer>
  );
};

export const DragOutSpot = styled(SpotItemWithBorder)`
  background-color: transparent;
  border: none;
  min-width: unset;
  height: unset;
  padding: 0;
  justify-content: center;
  margin: 0;
  border-radius: 100%;

  .order {
    background-color: ${colorsV2.blue100};
    color: ${colorsV2.white100};
    border-radius: 100%;
  }
`;

const DraggableSpotContainer = styled.div`
  position: absolute;
  .dragging {
    .spot-item-wrapper {
      background-color: ${colorsV2.white100};
    }
  }
`;

const MobileDraggableSpotContainer = styled.div`
  position: absolute;
`;

// =================================================================================
// Image
// =================================================================================

const Image = () => {
  const { no, spots, image, answers } = React.useContext(DragOutContext);
  const { width, height, url } = image;

  return (
    <ImgBorder key={url}>
      <Img loading="lazy" key={image.url} src={image.url} alt="" style={{ width, height }} />
      {spots.map((spot: Spot, spotIdx) => (
        <DraggableSpot
          id={`question-no-${no + spotIdx}`}
          spot={spot}
          key={spot.id}
          className={cl({ dragged: (answers || [])?.includes(spotIdx) })}
        />
      ))}
    </ImgBorder>
  );
};

const ImgBorder = styled.div`
  margin: 0 auto;
  width: fit-content;
  background: ${namedColors.pureWhite};
  box-sizing: border-box;
  border-radius: 8px;
  position: relative;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
`;

// =================================================================================
// QuestionMobile
// =================================================================================

interface QuestionProps {
  question: string;
  idx: number;
}

interface DragOutDrop {
  spot: Spot;
}

const Question = (props: QuestionProps) => {
  const { question, idx } = props;
  const { onAnswer, onRemoveAnswer, spots, no, answers } = React.useContext(DragOutContext);

  const [{ isOver }, drop] = useDrop({
    accept: QuestionTypeEnum.DRAG_OUT,
    drop: (item: DragOutDrop) => {
      const spotIdx = spots.findIndex((s) => s.id === item.spot.id);
      onAnswer(idx, spotIdx);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const ans = (answers || [])[idx];
  const isAnswered = isNumber(ans);

  return (
    <DroppableSpot
      ref={drop}
      text={`${no + idx}`}
      className={cl({ answered: isAnswered, over: isOver })}
    >
      <SpotPlaceholder className="placeholder">
        {isAnswered ? numberToABC(ans + 1) : null}
      </SpotPlaceholder>
      <QuestionContent>{question}</QuestionContent>
      <SmallCloseButton className="answer-remove-btn" onClick={() => onRemoveAnswer(idx)} />
    </DroppableSpot>
  );
};

const QuestionContent = styled.span`
  display: inline-block;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${colorsV2.black100};
`;

const DroppableSpot = styled(SpotItem)`
  display: grid;
  justify-content: start;
  margin-bottom: 12px;
  border: 1px solid transparent;
  padding: 4px 8px 4px 4px;
  .answer-remove-btn {
    display: none;
  }
  &:hover {
    border-color: ${colorsV2.gray20};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04);
    border-radius: 100px;

    &.answered .answer-remove-btn {
      display: inline-flex;
    }
  }

  .order {
    background: ${colorsV2.black100};
  }

  &.answered {
    grid-template-columns: max-content max-content 1fr max-content;

    .order {
      background-color: ${colorsV2.black100};
    }

    .placeholder {
      background-color: ${colorsV2.blue100};
      border: none;
    }
  }

  &.over {
    border-color: ${colorsV2.gray20};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04);
    border-radius: 100px;

    .placeholder {
      border: 1px solid ${colorsV2.blue100};
      background-color: ${colorsV2.blue20};
    }
  }
`;
